import { useEffect, useState } from 'react';
import { useCreateAccountContext } from './Context';
import { CreateAccountClient } from './FormClient';
import { CreateAccountEstab } from './FormEstab';
import { MailCheck } from 'lucide-react';

type CreateAccountProps = {
    initialTab?: 'estab' | 'client';
    clientOnly?: boolean;
};

const TABS: { key: 'client' | 'estab'; label: string; description: string }[] = [
    { key: 'client', label: 'Cliente', description: 'Agende horários e acompanhe seus atendimentos.' },
    { key: 'estab', label: 'Estabelecimento', description: 'Gerencie agenda, clientes e equipe do seu negócio.' },
];

export default function CreateAccount({ initialTab = 'client', clientOnly = false }: CreateAccountProps) {
    const { returnAction, sentToEmail } = useCreateAccountContext();
    const [activeTab, setActiveTab] = useState<'estab' | 'client'>(initialTab);

    useEffect(() => {
        setActiveTab(clientOnly ? 'client' : initialTab);
    }, [initialTab, clientOnly]);

    if (sentToEmail) {
        return (
            <div className="space-y-6 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
                    <MailCheck size={26} strokeWidth={1.75} />
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-semibold tracking-tight">Verifique seu e-mail</h1>
                    <p className="text-sm text-base-content/60">
                        Enviamos um link de ativação para{' '}
                        <span className="font-medium text-base-content">{sentToEmail}</span>. Abra o e-mail para
                        criar sua senha e acessar a conta.
                    </p>
                </div>
                <p className="text-xs text-base-content/50">Não encontrou? Confira a caixa de spam ou promoções.</p>
                <button type="button" onClick={returnAction} className="btn btn-primary h-12 w-full rounded-xl">
                    Voltar para entrar
                </button>
            </div>
        );
    }

    const active = TABS.find((tab) => tab.key === (clientOnly ? 'client' : activeTab)) || TABS[0];

    return (
        <div className="space-y-6">
            <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Criar conta</h1>
                <p className="text-sm text-base-content/60">{active.description}</p>
            </div>

            {/* Controle segmentado no padrao do sistema. */}
            {!clientOnly && <div
                role="tablist"
                aria-label="Tipo de conta"
                className="grid grid-cols-2 gap-0.5 rounded-[0.625rem] bg-[rgb(118_118_128/0.12)] p-0.5 dark:bg-[rgb(118_118_128/0.24)]"
            >
                {TABS.map((tab) => {
                    const isActive = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveTab(tab.key)}
                            className={`h-9 rounded-[0.5rem] text-sm font-medium transition-all duration-200 ${
                                isActive
                                    ? 'bg-base-100 text-base-content shadow-[0_1px_3px_rgb(0_0_0/0.12),0_0_0_0.5px_rgb(0_0_0/0.04)] dark:bg-[#636366]'
                                    : 'text-base-content/65 hover:text-base-content'
                            }`}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>}

            <div role="tabpanel">
                {!clientOnly && activeTab === 'estab' ? <CreateAccountEstab /> : <CreateAccountClient />}
            </div>

            <p className="text-center text-sm text-base-content/60">
                Já tem uma conta?{' '}
                <button type="button" onClick={() => returnAction()} className="font-medium text-primary hover:underline">
                    Entrar
                </button>
            </p>
        </div>
    );
}
